/**
 * Real account registration, called directly from the browser.
 *
 * This is NOT a portal mock — it hits Atlas's actual `/accounts/register` and
 * `/accounts/login`, the same identity system every GameCodex product shares
 * (portal-auth's README: "Atlas identity resolution, shared by every portal").
 * A visitor who registers here gets a real account with a real session, same
 * as they would on any other GameCodex product. That's deliberate: the joke
 * is the checkout that follows, not the account — this repo's whole premise
 * is that these parodies are genuinely functional.
 *
 * Direct browser → Atlas is safe because `/accounts/*` is CORS-open (`origin:
 * '*'` in atlas/src/server/index.ts) and issues nothing more sensitive than a
 * user's own session for their own account — unlike the LLM/commerce
 * gateways, which stay server-side behind a service token.
 */

const ATLAS_API_BASE =
  (import.meta as unknown as { env?: { VITE_ATLAS_API_BASE?: string } }).env?.VITE_ATLAS_API_BASE ??
  'https://atlas.gamecodex.com/api'

const SESSION_KEY = 'scratchyo.session'

export interface Session {
  accessToken: string
  account: { id: string; email: string; name: string | null }
}

export function getSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    return raw ? (JSON.parse(raw) as Session) : null
  } catch {
    return null
  }
}

function storeSession(s: Session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(s))
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY)
}

export type AuthResult = { ok: true; session: Session } | { ok: false; error: string }

async function post(path: string, body: unknown): Promise<AuthResult> {
  let res: Response
  try {
    res = await fetch(`${ATLAS_API_BASE}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch {
    return { ok: false, error: 'Could not reach the account service. Try again shortly.' }
  }

  const data = (await res.json().catch(() => null)) as
    | { accessToken?: string; account?: Session['account']; error?: string }
    | null

  if (!res.ok || !data?.accessToken || !data.account) {
    return { ok: false, error: data?.error ?? 'Something went wrong creating your session.' }
  }

  const session: Session = { accessToken: data.accessToken, account: data.account }
  storeSession(session)
  return { ok: true, session }
}

export function register(email: string, password: string, name?: string): Promise<AuthResult> {
  return post('/accounts/register', { email, password, name })
}

export function login(email: string, password: string): Promise<AuthResult> {
  return post('/accounts/login', { email, password })
}
